import * as React from 'react';
import { Image, Text, View, StyleSheet, Platform, Share, Pressable } from 'react-native';
import * as AppUtilities from "../context/AppUtilities";
import * as AppConstants from "../context/AppConstants";
import * as AppState from "../context/AppState";
import { AppContext } from "../context/AppContext";
import { Divider, List } from 'react-native-paper';
import Markdown from 'react-native-markdown-display-fh';
import { CustomScrollView } from '../components/CustomScrollView';
import { Collapsible } from '../components/Collapsible';
import { useForm, Controller } from "react-hook-form";
import { CustomButton } from '../components/CustomButton';
import { TextInput } from 'react-native-paper';
import { HeaderLeft } from '../components/HeaderLeft';

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
    padding: 20,
  },
  heading: {
    fontSize: 18,
    fontWeight: "bold",
  },
  label: {
    fontSize: 14,
    fontWeight: "bold",
    paddingRight: 5,
    flexWrap: "wrap",
  },
  label2: {
    fontSize: 14,
    fontWeight: "bold",
    paddingBottom: 2,
    flexWrap: "wrap",
  },
  labelPadLeft: {
    fontSize: 18,
    fontWeight: "bold",
    paddingLeft: 10,
    paddingTop: 5,
    flex: 1,
    flexWrap: "wrap",
  },
  normal: {
    fontSize: 14,
    paddingRight: 10,
    flexWrap: "wrap",
    flex: 1,
  },
  normal2: {
    fontSize: 14,
    //paddingRight: 10,
    flexWrap: "wrap",
    //flex: 1,
  },
  divider: {
    marginBottom: 10,
  },
  line: {
    flexDirection: "row",
    paddingBottom: 15,
    flexShrink: 1,
    alignItems: "center",
  },
  line2: {
    //flexDirection: "row",
    paddingBottom: 15,
    //flexShrink: 1,
    //alignItems: "center",
  },
  listItemStyle: {
    paddingLeft: 5,
  },
  certificationImage: {
    width: 85,
    marginRight: 10,
  },
  imageWrapper: {
    flexDirection: "row",
    flexWrap: "wrap",
    //justifyContent: "space-evenly",
  },
  imageContainer: {
    padding: 20,
  },
  link: {
    color: "#0000EE",
    textDecorationLine: "underline",
  },
  topSection: {
    flex: 1,
    flexDirection: "row",
  },
  topSectionLeftContainer: {
    flex: 1,
  },
  topSectionRightContainer: {
  },
  topSectionRight: {
    flexDirection: "row",
    justifyContent: "flex-end",
  },
  shareImage: {
    margin: 10,
    width: 25,
    height: 25,
  },
});

const markdownRules = {
  text: (node, children, parent, styles, inheritedStyles = {}) => (
    <Text key={node.key} style={[inheritedStyles, styles.text]} selectable={true}>
      {node.content}
    </Text>
  ),
};

function getCertificationImage(filter) {
  if (AppUtilities.DEBUGLEVEL.product) {
    AppUtilities.debug("ProductDetails getCertificationImage filter: " + filter);
  }

  return AppUtilities.getCertificationImage(filter, styles.certificationImage);
}

function getProductWebsiteLink(product) {
  let website = product.product_website;
  if (website && product.product_website_underlying) {
    website = product.product_website_underlying;
  } else if (!website && AppUtilities.isMarkdownLink(product.certification)) {
    website = AppUtilities.getMarkdownLink(product.certification);
  }
  return website;
}

function getProductWebsiteText(product) {
  let website = product.product_website;
  if (!website && AppUtilities.isMarkdownLink(product.certification)) {
    website = AppUtilities.getMarkdownLinkText(product.certification);
  }
  return website;
}

function hasProductWebsite(product) {
  return product && !AppUtilities.isBlank(getProductWebsiteLink(product));
}

const openShare = (name, link) => {
  Share.share(
    {
      message: link,
      url: link,
      title: name,
    },
    {
      dialogTitle: "Share Find Humane Link",
      subject: name,
    }
  );
};

export function ProductDetails({ route, navigation }) {
  const { showPermanentSnack, showTemporarySnack, hideSnack, getLoginState, showSpinner, hideSpinner } = React.useContext(AppContext);
  const [images, setImages] = React.useState([]);
  const [details, setDetails] = React.useState({});
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (navigation) {
      return navigation.addListener("focus", () => {
        if (route && route.params && route.params.productid) {
          AppUtilities.recordAppPageView("product/" + route.params.productid);
        }
      });
    }
  }, [navigation]);

  React.useEffect(() => {
    if (navigation) {
      return navigation.addListener("blur", () => {
      });
    }
  }, [navigation]);

  async function loadProductDetails() {
    try {
      showSpinner();
      hideSnack();
      setLoading(true);

      AppUtilities.info("ProductDetails.loadProductDetails fetching details");

      const response = await AppUtilities.fetchUnauthorized(
        "ProductDetails.loadProductDetails",
        "/find_humane/api/get_product_details?product_id=" + route.params.productid,
      );
      if (response.body) {
        hideSpinner();

        //AppUtilities.info("ProductDetails.loadProductDetails received: " + AppUtilities.toJSONPretty(response.body.details));

        navigation.setOptions({
          headerLeft: (props) => <HeaderLeft navigation={navigation} title={response.body.details.product_name} />,
          title: response.body.details.product_name,
        });

        if (AppUtilities.DEBUGLEVEL.product) {
          AppUtilities.debug("ProductDetails loaded product " + AppUtilities.toJSONPretty(response.body.details));
        }

        setDetails(response.body.details);
        //AppUtilities.dir(response.body.details);

        if (response.body.details.files.length > 0) {
          // We'll need the image dimensions
          loadImageSizes(response.body.details.files);
        }
      } else {
        throw new Error("Result body not found");
      }
    } catch (e) {
      showPermanentSnack(AppUtilities.getErrors(e), "Retry", loadProductDetails);
      hideSpinner();
      AppUtilities.handleException(e, "ProductDetails.loadProductDetails");
    } finally {
      setLoading(false);
    }
  }

  React.useEffect(() => {
    loadProductDetails();
  }, []);

  function getImageSize(uri, id) {
    return new Promise(function(resolve, reject) {
      Image.getSize(
        uri,
        (width, height) => {
          resolve({ id, width, height });
        },
        error => {
          resolve({ id, width: 0, height: 0 });
          //reject(error);
        }
      );
    });
  };

  async function loadImageSizes(files) {
    const imageInfos = await Promise.all(files.map((file) => {
      const url = AppUtilities.getRootUri() + "find_humane/api/get_product_file_thumbnail?product_id=" + route.params.productid + "&file_id=" + file.id;
      if (AppUtilities.DEBUGLEVEL.product) {
        AppUtilities.debug("ProductDetails.useEffect: Getting dimensions for " + url);
      }
      return getImageSize(url, file.id);
    }));
    if (AppUtilities.DEBUGLEVEL.product) {
      AppUtilities.debug("ProductDetails.useEffect: images with dimensions: " + AppUtilities.toJSONPretty(imageInfos));
    }
    setImages(imageInfos);
  }

  function getBoolean(name, val, hideIfFalse) {
    if (hideIfFalse && !val) {
      return null;
    }
    return (
      <View style={styles.line2}>
        <Text selectable={true} style={styles.label2}>{name}:</Text>
        <Text selectable={true} style={styles.normal2}>{val ? "Yes" : "No"}</Text>
      </View>
    );
  }

  function getBooleanSimple(name, val, hideIfFalse) {
    if (hideIfFalse && !val) {
      return null;
    }
    return (
      <View style={styles.line}>
        <Text selectable={true} style={styles.label}>{name}</Text>
      </View>
    );
  }

  function getGeneric(name, val, hideIfFalse) {
    if (hideIfFalse) {
      if (AppUtilities.isString(val) && AppUtilities.isBlank(val)) {
        return null;
      } else if (!val && val != 0) {
        return null;
      }
    }
    return (
      <View style={styles.line}>
        <Text selectable={true} style={styles.label}>{name}:</Text>
        <Text selectable={true} style={styles.normal}>{val}</Text>
      </View>
    );
  }

  const { control, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: {
      email: "",
      details: "",
    }
  });

  async function onSubmit(data) {
    try {
      showSpinner();
      hideSnack();

      await AppUtilities.fetchUnauthorized(
        "ProductDetails.onSubmit",
        "/find_humane/api/contact",
        "POST",
        {
          email: data.email,
          data: data.details,
          subject: "Report for " + details.product_name + " (" + details.id + ")",
        }
      );

      showTemporarySnack("Thanks! We'll review soon.");

      reset();

    } catch (e) {
      showPermanentSnack(AppUtilities.getErrors(e), "Retry", () => onSubmit(data));
      AppUtilities.handleException(e, "ProductDetails.onSubmit");
    } finally {
      hideSpinner();
    }

  }

  async function onValidationErrors(errors) {
    showTemporarySnack("There are some errors in the form");
  }

  if (loading || !details) {
    return null;
  }

  return (
    <CustomScrollView contentContainerStyle={AppConstants.STYLES.backgroundWhite}>
      <View style={styles.topSection}>
        <View style={styles.topSectionLeftContainer}>
          {/*
            details.product_name && details.product_name.length > AppConstants.HEADER_TEXT_OVERFLOW_LENGTH ?
                <View style={styles.line}>
                  <Text selectable={true} style={styles.label}>Product:</Text>
                  <Text selectable={true} style={styles.normal}>{details.product_name ? details.product_name : "Loading..."}</Text>
                </View>
              :
                null
          */}

          {
            details.producer && details.producer.length > 0 ?
                <View style={styles.line}>
                  <Text selectable={true} style={styles.label}>Producer:</Text>
                  {
                    AppUtilities.createInternalLink(
                      navigation,
                      details.producer,
                      route.params.online ?
                          AppConstants.SCREENS.PRODUCER_ONLINE_DETAILS
                        :
                          route.params.explore ?
                              AppConstants.SCREENS.PRODUCER_EXPLORE_DETAILS
                            :
                              AppConstants.SCREENS.PRODUCER_DETAILS,
                      {
                        producer: details.producer,
                        online: route.params.online,
                        explore: route.params.explore,
                      }
                    )
                  }
                </View>
              :
                null
          }

          {
            details.partiallyhumane ?
                <View style={styles.line}>
                  <Image source={require('../../assets/circle-half-full-black-30.png')} style={AppConstants.STYLES.image30x30} />
                  <Text selectable={true} style={styles.labelPadLeft}>Partial: Not all animal ingredients humanely certified</Text>
                </View>
              :
                null
          }
          
          <View style={styles.line}>
            {
              details.animal_welfare_approved ?
                  getCertificationImage("animal_welfare_approved")
                :
                  null
            }
            {
              details.agw_certified_grassfed ?
                  getCertificationImage("agw_certified_grassfed")
                :
                  null
            }
            {
              details.agw_certified_non_gmo ?
                  getCertificationImage("agw_certified_non_gmo")
                :
                  null
            }
            {
              details.agw_certified_organic ?
                  getCertificationImage("agw_certified_organic")
                :
                  null
            }
            {
              details.agw_certified_regenerative ?
                  getCertificationImage("agw_certified_regenerative")
                :
                  null
            }
            {
              details.agw_salmon_welfare_certified ?
                  getCertificationImage("agw_salmon_welfare_certified")
                :
                  null
            }
            {
              details.certified_humane_pasture_raised_grass_fed ?
                  getCertificationImage("certified_humane_pasture_raised_grass_fed")
                :
                  details.certified_humane_grass_fed ?
                      getCertificationImage("certified_humane_grass_fed")
                    :
                      details.certified_humane_free_range ?
                          getCertificationImage("certified_humane_free_range")
                        :
                          details.certified_humane ?
                              getCertificationImage("certified_humane")
                            :
                              null
            }
            {
              details.certified_humane_approved_hatcheries ?
                  getCertificationImage("certified_humane_approved_hatcheries")
                :
                  null
            }
            {
              details.animal_welfare_certified_6 ?
                  getCertificationImage("animal_welfare_certified_6")
                :
                  details.animal_welfare_certified_5 ?
                    getCertificationImage("animal_welfare_certified_5")
                  :
                    details.animal_welfare_certified_4 ?
                        getCertificationImage("animal_welfare_certified_4")
                      :
                        details.animal_welfare_certified_3 ?
                            getCertificationImage("animal_welfare_certified_3")
                          :
                            details.animal_welfare_certified_2 ?
                                getCertificationImage("animal_welfare_certified_2")
                              :
                                details.animal_welfare_certified_1 ?
                                    getCertificationImage("animal_welfare_certified_1")
                                  :
                                    null
            }
            {
              details.better_chicken_project ?
                  getCertificationImage("better_chicken_project")
                :
                  null
            }
            {
              details.animal_welfare_certified_fish_6 ?
                  getCertificationImage("animal_welfare_certified_fish_6")
                :
                  details.animal_welfare_certified_fish_3 ?
                    getCertificationImage("animal_welfare_certified_fish_3")
                  :
                    details.animal_welfare_certified_fish_1 ?
                        getCertificationImage("animal_welfare_certified_fish_1")
                      :
                        null
            }
            {
              details.applegate_humanely_raised ?
                  getCertificationImage("applegate_humanely_raised")
                :
                  null
            }
            {
              details.usda_organic ?
                  getCertificationImage("usda_organic")
                :
                  null
            }
            {
              details.american_grassfed_association ?
                  getCertificationImage("american_grassfed_association")
                :
                  null
            }
            {
              details.american_humane_certified ?
                  getCertificationImage("american_humane_certified")
                :
                  null
            }
            {
              details.food_alliance_certified ?
                  getCertificationImage("food_alliance_certified")
                :
                  null
            }
            {
              details.one_health_certified ?
                  getCertificationImage("one_health_certified")
                :
                  null
            }
            {
              details.regenerative_organic_certified ?
                  getCertificationImage("regenerative_organic_certified")
                :
                  null
            }
          </View>
        </View>
        <View style={styles.topSectionRightContainer}>
          <View style={styles.topSectionRight}>
            {
              details ?
                  AppUtilities.isWeb() ?
                      AppUtilities.createLink(<Image source={require('../../assets/share-black.png')} style={styles.shareImage} />, "mailto:?subject=" + encodeURIComponent(details.product_name) + "&body=" + encodeURIComponent("Check out this more humanely raised animal-based product, " + details.product_name + ": " + AppConstants.HOMEPAGE_LINK + "product/" + route.params.productid))
                    :
                      <Pressable onPress={() => openShare(details.product_name, AppConstants.HOMEPAGE_LINK + "product/" + route.params.productid)}>
                        <Image source={require('../../assets/share-black.png')} style={styles.shareImage} />
                      </Pressable>
                :
                  null
            }
          </View>
        </View>
      </View>

      <Divider style={AppConstants.STYLES.marginVertical10} />

      { getGeneric("Price (may vary)", details.price_amount, true) }
      { getGeneric("Price Information (may vary)", details.price_details, true) }

      {
        hasProductWebsite(details) ?
            <View style={{ marginBottom: 10 }}>
              {
                AppUtilities.isWeb() && false ?
                    AppUtilities.createLink(
                      <CustomButton
                        style={{ marginBottom: 10 }}
                      >
                        More Information / Buy
                      </CustomButton>,
                      getProductWebsiteLink(details),
                      AppConstants.UTM_CAMPAIGNS.PRODUCT_DETAILS,
                      null,
                      null,
                      {
                        textDecoration: "none",
                      },
                    )
                  :
                    <CustomButton
                      style={{ marginBottom: 10 }}
                      onPress={() => AppUtilities.openLink(getProductWebsiteLink(details), AppConstants.UTM_CAMPAIGNS.PRODUCT_DETAILS)}
                    >
                      More Information / Buy
                    </CustomButton>
              }
              {AppUtilities.createLink(getProductWebsiteText(details), getProductWebsiteLink(details), AppConstants.UTM_CAMPAIGNS.PRODUCT_DETAILS)}
            </View>
          :
            null
      }

      {
        details.certification && details.certification.length > 0 ?
            <View>
              {/*<Text selectable={true} style={styles.label}>More Information:</Text>*/}
              <View style={AppConstants.STYLES.paddingTop10Bottom15}>
                <Markdown rules={markdownRules}>
                  {details.certification}
                </Markdown>
              </View>
            </View>
          :
            null    
      }

      {
        images.length > 0 ?
            <>
                <Divider style={AppConstants.STYLES.marginVertical10} />
                <View style={styles.imageWrapper}>
                {
                  images.map((imageInfo) => {
                    return (
                      <Pressable
                        key={"touch" + imageInfo.id}
                        onPress={() => {
                          AppUtilities.openLink(AppUtilities.getRootUri() + "find_humane/api/get_product_file?product_id=" + details.id + "&file_id=" + imageInfo.id);
                        }}
                        style={styles.imageContainer}
                      >
                        <Image
                          key={imageInfo.id}
                          source={{ uri: AppUtilities.getRootUri() + "find_humane/api/get_product_file_thumbnail?product_id=" + details.id + "&file_id=" + imageInfo.id }}
                          style={{ width: imageInfo.width, height: imageInfo.height, maxWidth: "100%" }}
                          resizeMode="contain"
                        />
                      </Pressable>
                    );
                  })
                }
              </View>
            </>
          :
            null
      }

      {/*
      <Divider style={AppConstants.STYLES.marginVertical10} />
      { getBoolean(AppConstants.CERTIFICATION_NAMES.ANIMAL_WELFARE_APPROVED, details.animal_welfare_approved, true) }
      { getBoolean(AppConstants.CERTIFICATION_NAMES.AGW_CERTIFIED_GRASSFED, details.agw_certified_grassfed, true) }
      { getBoolean(AppConstants.CERTIFICATION_NAMES.AGW_CERTIFIED_ORGANIC, details.agw_certified_organic, true) }
      { getBoolean(AppConstants.CERTIFICATION_NAMES.AGW_CERTIFIED_REGENERATIVE, details.agw_certified_regenerative, true) }
      { getBoolean(AppConstants.CERTIFICATION_NAMES.AGW_CERTIFIED_NONGMO, details.agw_certified_non_gmo, true) }
      { getBoolean(AppConstants.CERTIFICATION_NAMES.AGW_SALMON_WELFARE_CERTIFIED, details.agw_salmon_welfare_certified, true) }
      {
        details.certified_humane_pasture_raised_grass_fed ?
            getBoolean(AppConstants.CERTIFICATION_NAMES.CERTIFIED_HUMANE + " + Pasture-raised", details.certified_humane_pasture_raised_grass_fed, true)
          :
            details.certified_humane_grass_fed ?
                getBoolean(AppConstants.CERTIFICATION_NAMES.CERTIFIED_HUMANE + " + Grass-fed", details.certified_humane_grass_fed, true)
              :
                details.certified_humane_free_range ?
                    getBoolean(AppConstants.CERTIFICATION_NAMES.CERTIFIED_HUMANE + " + Free-range", details.certified_humane_free_range, true)
                  :
                    getBoolean(AppConstants.CERTIFICATION_NAMES.CERTIFIED_HUMANE, details.certified_humane, true)
      }
      {
        details.animal_welfare_certified_6 ?
            getBoolean(AppConstants.CERTIFICATION_NAMES.GAP + " Step 5+", details.animal_welfare_certified_6, true)
          :
            details.animal_welfare_certified_5 ?
                getBoolean(AppConstants.CERTIFICATION_NAMES.GAP + " Step 5", details.animal_welfare_certified_5, true)
              :
                details.animal_welfare_certified_4 ?
                    getBoolean(AppConstants.CERTIFICATION_NAMES.GAP + " Step 4", details.animal_welfare_certified_4, true)
                  :
                    details.animal_welfare_certified_3 ?
                        getBoolean(AppConstants.CERTIFICATION_NAMES.GAP + " Step 3", details.animal_welfare_certified_3, true)
                      :
                        details.animal_welfare_certified_2 ?
                            getBoolean(AppConstants.CERTIFICATION_NAMES.GAP + " Step 2", details.animal_welfare_certified_2, true)
                          :
                            getBoolean(AppConstants.CERTIFICATION_NAMES.GAP + " Step 1", details.animal_welfare_certified_1, true)
      }
      {
        details.animal_welfare_certified_fish_6 ?
            getBoolean(AppConstants.CERTIFICATION_NAMES.GAP_FISH + " Step 5+", details.animal_welfare_certified_fish_6, true)
          :
            details.animal_welfare_certified_fish_3 ?
                getBoolean(AppConstants.CERTIFICATION_NAMES.GAP_FISH + " Step 3", details.animal_welfare_certified_fish_3, true)
              :
                getBoolean(AppConstants.CERTIFICATION_NAMES.GAP_FISH + " Step 1", details.animal_welfare_certified_fish_1, true)
      }
      { getBoolean(AppConstants.CERTIFICATION_NAMES.APPLEGATE_HUMANELY_RAISED, details.applegate_humanely_raised, true) }
      { getBoolean(AppConstants.CERTIFICATION_NAMES.AMERICAN_HUMANE_CERTIFIED, details.american_humane_certified, true) }
      { getBoolean(AppConstants.CERTIFICATION_NAMES.AMERICAN_GRASSFED_ASSOCIATION, details.american_grassfed_association, true) }
      { getBoolean(AppConstants.CERTIFICATION_NAMES.USDA_ORGANIC, details.usda_organic, true) }
      { getBoolean(AppConstants.CERTIFICATION_NAMES.FOOD_ALLIANCE_CERTIFIED, details.food_alliance_certified, true) }
      { getBoolean(AppConstants.CERTIFICATION_NAMES.ONE_HEALTH_CERTIFIED, details.one_health_certified, true) }
      { getBoolean(AppConstants.CERTIFICATION_NAMES.SELF_CERTIFIED, details.self_certified, true) }
      */ }

      {
        details.beef ||
          details.chicken ||
          details.pork ||
          details.fish ||
          details.eggs ||
          details.eggs_duck ||
          details.eggs_turkey ||
          details.dairy ||
          details.dairygoat ||
          details.dairysheep ||
          details.cheese ||
          details.cheesegoat ||
          details.cheesesheep ||
          details.sweet ||
          details.pantry ||
          details.bath_body ||
          details.goat ||
          details.bison ||
          details.accessories ||
          details.homegoods ||
          details.leather ||
          details.clothing ||
          details.duck ||
          details.turkey ||
          details.mutton ||
          details.lamb ||
          details.veal ||
          details.dogfood ||
          details.catfood ||
          details.farmstay ||
          details.venison ?
            <Divider style={styles.divider} />
          :
            null
      }
      { getBooleanSimple("Cow", details.beef, true) }
      { getBooleanSimple("Chicken", details.chicken, true) }
      { getBooleanSimple("Pork", details.pork, true) }
      { getBooleanSimple("Fish", details.fish, true) }
      { getBooleanSimple("Eggs (Chicken)", details.eggs, true) }
      { getBooleanSimple("Eggs (Duck)", details.eggs_duck, true) }
      { getBooleanSimple("Eggs (Turkey)", details.eggs_turkey, true) }
      { getBooleanSimple("Dairy (Cow Milk)", details.dairy, true) }
      { getBooleanSimple("Dairy (Goat Milk)", details.dairygoat, true) }
      { getBooleanSimple("Dairy (Sheep Milk)", details.dairysheep, true) }
      { getBooleanSimple("Cheese (Cow Milk)", details.cheese, true) }
      { getBooleanSimple("Cheese (Goat Milk)", details.cheesegoat, true) }
      { getBooleanSimple("Cheese (Sheep Milk)", details.cheesesheep, true) }
      { getBooleanSimple("Goat", details.goat, true) }
      { getBooleanSimple("Bison", details.bison, true) }
      { getBooleanSimple("Duck", details.duck, true) }
      { getBooleanSimple("Turkey", details.turkey, true) }
      { getBooleanSimple("Mutton", details.mutton, true) }
      { getBooleanSimple("Lamb", details.lamb, true) }
      { getBooleanSimple("Veal", details.veal, true) }
      { getBooleanSimple("Venison", details.venison, true) }
      { getBooleanSimple("For Dogs", details.dogfood, true) }
      { getBooleanSimple("For Cats", details.catfood, true) }
      { getBooleanSimple("Sweets & Baked Goods", details.sweet, true) }
      { getBooleanSimple("Pantry Foods", details.pantry, true) }
      { getBooleanSimple("Bath, Body & Skin Care", details.bath_body, true) }
      { getBooleanSimple("Clothing", details.clothing, true) }
      { getBooleanSimple("Accessories", details.accessories, true) }
      { getBooleanSimple("Home Goods", details.homegoods, true) }
      { getBooleanSimple("Leather, Hide & Skins", details.leather, true) }
      { getBooleanSimple("Overnight Farmstay", details.farmstay, true) }

      {
        details.ingredients && details.ingredients.length > 0 ?
            <View>
              <Text selectable={true} style={styles.label}>Details:</Text>
              <View style={AppConstants.STYLES.paddingTop10Bottom15}>
                <Markdown rules={markdownRules}>
                  {details.ingredients}
                </Markdown>
              </View>
            </View>
          :
            null
      }

      {
        details.pastureraised ||
          details.freerange ||
          details.grassfed ||
          details.cagefree ||
          details.nocrates ||
          details.grassfinished ||
          details.restorativefarms ||
          details.nohormones ||
          details.noantibiotics ||
          details.nopesticides ||
          details.organic ||
          details.nongmo ||
          details.nongmoproject ||
          details.smallfamilyfarms ||
          details.bcorporation ||
          details.slaughtered_without_transport ||
          details.unvaccinated ||
          details.nomaleculling ||
          details.nobeaktrimming ||
          details.weaned_naturally ||
          details.humanely_hatched ||
          details.consistentdaylight ||
          details.vegetariandiet ?
            <Divider style={styles.divider} />
          :
            null
      }
      { getBooleanSimple("Pasture Raised", details.pastureraised, true) }
      { !details.pastureraised ? getBooleanSimple("Free Range", details.freerange, true) : null }
      { getBooleanSimple("Grass Fed", details.grassfed, true) }
      { getBooleanSimple("Grass Finished", details.grassfinished, true) }
      { getBooleanSimple("No cages", details.cagefree, true) }
      { getBooleanSimple("No crates", details.nocrates, true) }
      { getBooleanSimple("Regenerative/Restorative farms", details.restorativefarms, true) }
      { getBooleanSimple("No hormones", details.nohormones, true) }
      { getBooleanSimple("No antibiotics", details.noantibiotics, true) }
      { getBooleanSimple("No pesticides", details.nopesticides, true) }
      { getBooleanSimple("No male culling", details.nomaleculling, true) }
      { getBooleanSimple("No beak trimming", details.nobeaktrimming, true) }
      { getBooleanSimple("Consistent daylight access", details.consistentdaylight, true) }
      { /*getBooleanSimple("Organic", details.organic, true)*/ }
      {
        details.nongmoproject ?
            getBooleanSimple("Non-GMO Project Verified", details.nongmoproject, true)
          :
            getBooleanSimple("Non-GMO", details.nongmo, true)
      }
      { getBooleanSimple("Small family farm(s)", details.smallfamilyfarms, true) }
      { getBooleanSimple("B Corporation", details.bcorporation, true) }
      { getBooleanSimple("Slaughtered on-farm", details.slaughtered_without_transport, true) }
      { getBooleanSimple("Unvaccinated", details.unvaccinated, true) }
      { getBooleanSimple("Weaned naturally", details.weaned_naturally, true) }
      { getBooleanSimple("Humanely Hatched®", details.humanely_hatched, true) }
      { getBooleanSimple("Vegetarian Diet for Animals", details.vegetariandiet, true) }

      {
        details.spiciness ||
          details.zerocarbs ||
          details.ketofriendly ||
          details.paleofriendly ||
          details.dairyfree ||
          details.microwavable ||
          details.fullycooked ||
          details.frozen ||
          details.nocasein ||
          details.glutenfree ||
          details.nonitrates ||
          details.nosoy ||
          details.nocorn ||
          details.lowsodium ||
          details.lowfat ||
          details.nopreservatives ||
          details.whole30approved ||
          details.noartificial ||
          details.certified_paleo ||
          details.keto_certified ||
          details.cornucopia_institute_rating ||
          details.halal ||
          details.raw ||
          details.notonline ||
          details.solarpanels ||
          details.carbonneutralcertified ||
          details.livewebcam ||
          details.upcycledfeed ||
          details.recyclablepackaging ||
          details.kosher ?
            <Divider style={styles.divider} />
          :
            null
      }
      { getGeneric("Spiciness", details.spiciness, true) }
      { getBooleanSimple("Zero Sugars", details.zerocarbs, true) }
      { getBooleanSimple("Paleo-friendly", details.ketofriendly, true) }
      { getBooleanSimple("Keto-friendly", details.paleofriendly, true) }
      { getBooleanSimple("Dairy-free", details.dairyfree, true) }
      { getBooleanSimple("Microwavable", details.microwavable, true) }
      { getBooleanSimple("Fully Cooked", details.fullycooked, true) }
      { getBooleanSimple("Frozen", details.frozen, true) }
      { getBooleanSimple("Casein-free", details.nocasein, true) }
      { getBooleanSimple("Gluten-free", details.glutenfree, true) }
      { getBooleanSimple("No nitrates/nitrites", details.nonitrates, true) }
      { getBooleanSimple("Soy-free", details.nosoy, true) }
      { getBooleanSimple("Corn-free", details.nocorn, true) }
      { getBooleanSimple("Kosher", details.kosher, true) }
      { getBooleanSimple("Halal", details.halal, true) }
      { getBooleanSimple("Low sodium", details.lowsodium, true) }
      { getBooleanSimple("Low fat", details.lowfat, true) }
      { getBooleanSimple("No preservatives", details.nopreservatives, true) }
      { getBooleanSimple("Whole30 Approved®", details.whole30approved, true) }
      { getBooleanSimple("No artificial ingredients", details.noartificial, true) }
      { getBooleanSimple("Raw", details.raw, true) }
      { getBooleanSimple("Certified Paleo®", details.certified_paleo, true) }
      { getBooleanSimple("Keto Certified™", details.keto_certified, true) }
      { getBooleanSimple("Certified CarbonNeutral®", details.carbonneutralcertified, true) }
      { getBooleanSimple("Live webcam of animals", details.livewebcam, true) }
      { getBooleanSimple("Solar panel use", details.solarpanels, true) }
      { getBooleanSimple("Up-cycled feed", details.upcycledfeed, true) }
      { getBooleanSimple("Recyclable packaging", details.recyclablepackaging, true) }
      { getBooleanSimple("Not Available for Delivery", details.notonline, true) }
      { getGeneric("Cornucopia Institute Rating", details.cornucopia_institute_rating, true) }

      <Divider style={AppConstants.STYLES.marginBottom20} />

      <Collapsible title="Report a problem or update">
        <Controller
          control={control}
          name="email"
          rules={{
            required: true,
          }}
          render={({ field: { onChange, onBlur, value } }) => (
            <TextInput
              label="Your email (in case of questions)"
              value={value}
              onChangeText={onChange}
              style={AppConstants.STYLES.width100PmarginVertical10borderNone}
              mode="outlined"
              dense={true}
              returnKeyType="send"
              onBlur={onBlur}
              autoComplete="email"
              keyboardType="email-address"
              textContentType="emailAddress"
            />
          )}
        />
        {errors.email && <Text selectable={true} style={AppConstants.STYLES.fontBoldColorRed}>Email is required.</Text>}

        <Controller
          control={control}
          name="details"
          rules={{
            //required: true,
          }}
          render={({ field: { onChange, onBlur, value } }) => (
            <TextInput
              label="Report"
              value={value}
              onChangeText={onChange}
              style={AppConstants.STYLES.width100PmarginVertical10borderNoneMinHeight}
              mode="outlined"
              dense={true}
              returnKeyType="send"
              onBlur={onBlur}
              multiline={true}
              numberOfLines={5}
              textAlignVertical="top"
            />
          )}
        />
        {errors.details && <Text selectable={true} style={AppConstants.STYLES.fontBoldColorRed}>Report required.</Text>}

        <CustomButton
          onPress={handleSubmit(onSubmit, onValidationErrors)}
          style={AppConstants.STYLES.paddingVertical10}
        >Submit</CustomButton>
      </Collapsible>
    </CustomScrollView>
  );
}
